<script setup>
import SecureTrustElement from '@/Components/Payments/SecureTrustElement.vue'
import { LibraryIcon, CreditCardIcon, XIcon, CashIcon } from '@heroicons/vue/outline'
import SavedCards from '@/Components/Payments/SavedCards.vue'
import SavedBankAccounts from '@/Components/Payments/SavedBankAccounts.vue'
import CardForm from '@/Components/Payments/CardForm.vue'
import BankForm from '@/Components/Payments/BankForm.vue'

import { ref, computed } from 'vue'
import { Inertia } from '@inertiajs/inertia'

import { useAuthStore } from '@/Stores/auth'
const auth = useAuthStore()
import { useErrorsStore } from '@/Stores/errors'
const errors = useErrorsStore()
import { usePaymentsStore } from '@/Stores/payments'
const payments = usePaymentsStore()

// convert the payment store state to refs so we can watch them and open the dialog
import { storeToRefs } from 'pinia'
const { showingCardForm, invoice } = storeToRefs(payments)

// template refs
const dialog = ref(null) // so we can open and close it
let payButton = ref(null)
let cardForm = ref(null)

const paymentAmount = computed(() => {
	return invoice.value.AmountDue
})

function pay() {
		
	// start a timer so we can show our takingAWhile text
	processTimer.value = setTimeout(() => {
		takingAWhile.value = true
	}, 3500)
	
	if (!payments.selectedCard) {
		// we have not selected a card, assume we are submitting the card form
		payButton.value.waitFor(cardForm.value.submitCardForm())
		.finally(() => {
			clearTimeout(processTimer)
		})
	} else {
		// we have a card selected, so we can pay

		payButton.value.waitFor(
			axios.post('/charge', {
				amount: paymentAmount.value,
				cardable_type: payments.cardable_type,
				cardable_id: payments.cardable_id,
				class: 'App\\Models\\TokenisedCreditCard',
				id: payments.selectedCard.id,
				invoice_number: invoice.value.InvoiceNumber,
				invoice_id: invoice.value.InvoiceID,
			}).then(response => {
				paymentSuccessful()
			}).catch(error => {
				closeDialog()
				errors.push({
					type: 'error',
					title: 'Payment Failed',
					message: 'Your payment failed',
				})
			})
			.finally(() => {
				clearTimeout(processTimer.value)
			})
		)
	}
}

function closeDialog() {
	payments.hideCardForm()
}

document.addEventListener("keydown", (e) => {
	if (e.key === "Escape") {
		if( showingCardForm.value ){
			closeDialog()
		}
	}
});

function paymentSuccessful() {
	closeDialog()
	errors.push({
		type: 'success',
		title: 'Payment Successful',
		message: 'Your payment was successful',
	})
	Inertia.reload()
}

const takingAWhile = ref(false)
const processTimer = ref(null)
</script>

<template>
    <div
		class="card-dialog fixed inset-0 z-10 grid overflow-y-scroll place-items-center"
		v-if="showingCardForm"
		@click="closeDialog"
		data-testid="card-dialog"
	>

		<div class=" rounded-md border  bg-white " @click.stop>
			<div class="w-full flex flex-col items-end -mb-6">
				<XIcon class="w-6 h-6 text-gray-500 cursor-pointer m-2" @click="closeDialog" />
			</div>			
			<div class="box-content card-dialog-inner">
				<div v-if="!auth.can('make_payments', 'organisations', payments.cardable_id)" class="text-center  text-gray-500 py-12">
					User does not have permission to make payments
				</div>
				<div v-else>
					<Heading size="2">Make a payment:</Heading>
					<div v-if="payments.invoice" class="flex flex-row justify-between w-full">
						<div>
							<Label>Invoice Number</Label>
							<Heading size="3">
								{{ invoice.InvoiceNumber?.length ? invoice.InvoiceNumber : "-" }}
							</Heading>
						</div>
						<div>
							<Label>Amount due</Label>
							<Heading size="3">
								${{ paymentAmount.toFixed(2) }}
							</Heading>
						</div>
					</div>
					
					<div class="italic text-sm" v-if="!auth.can('manage_payment_methods', 'organisations', payments.cardable_id)">
						User does not have permission to add payment methods
					</div>
					<div v-else-if="payments.savedCards && payments.savedCards.length">
						<SavedCards
							:cardable-id="payments.cardable_id"
							@select-card="payments.selectCard"
							:selected-card="payments.selectedCard"
						/>

						<div class="flex self-end mt-[2px] text-right mb-4 text-xs italic text-gray-700" v-if="auth.can('manage_payment_methods', 'organisations', payments.cardable_id)">
							<span>Saved payment methods are securely encrypted and stored offsite. You can manage saved payment methods via your <Link class="inline" href="/cards">payment methods page</Link></span>
						</div>
					</div>
							
					<div v-if="!payments.selectedCard">
						<Heading size="4">Enter a new credit card:</Heading>
						
						<CardForm							
							:cardable-type="payments.cardable_type"
							:cardable-id="payments.cardable_id"
							:amount="paymentAmount"
							@payment-successful="paymentSuccessful"
							ref="cardForm"
						/>
						
					</div>
					<div class="w-full flex flex-col items-end">
						<SecureTrustElement class="mt-4" />
						
						<p class="text-sm italic">Your account will be debited ${{ paymentAmount.toFixed(2) }}</p>
				
						<div class="flex flex-row justify-between items-end">

							<div v-if="takingAWhile" class="text-xs italic text-gray-500 max-w-[60%]">
								<Spinner color="blue" class="h-3 w-3 mr-1" />
								Still processing - please do not close or refresh the window
							</div>

							<PromiseButton
								ref="payButton"
								@click="pay"
								class="mt-4"
								data-testid="pay-now-button"
							>
								<CashIcon class="h-4 w-4 mr-2" /> Pay Now
							</PromiseButton>
						</div>
					</div>
				</div>
			</div>


        </div>
    </div>
</template>

<style>
.card-dialog {
	background: rgba(0,0,0,0.5);
}
@media (min-width: 420px) {
	.card-dialog-inner {
		width: calc(370px + 4rem);
		padding: 2rem;
	}
}
</style>